import { ReactNode, useEffect } from 'react';
import { Box, DialogContentText } from '@mui/material';
import {
  ExtendedDialog,
  NonFormDialogProps,
} from 'ui-component/extended/ExtendedDialog';
import { GenericResource, ToggleMutation } from 'types/api';
import useToggleRecord, {
  TogglePayloadFn,
} from 'hooks/clientV2/useToggleRecordDialog';

export enum ToggleRecordActionTypes {
  APPROVE = 'APPROVE',
  ARCHIVE = 'ARCHIVE',
  LOCK = 'LOCK',
  VOID = 'VOID',
  CLOSE = 'CLOSE',
}

export const ToggleActionStrings: {
  [key in ToggleRecordActionTypes]: ToggleRecordActionStrings;
} = {
  [ToggleRecordActionTypes.APPROVE]: {
    toggleToTrue: 'Approve',
    toggleToFalse: 'Unapprove',
    successToggledTrue: 'Approved',
    successToggledFalse: 'Unapproved',
  },
  [ToggleRecordActionTypes.ARCHIVE]: {
    toggleToTrue: 'Archive',
    toggleToFalse: 'Unarchive',
    successToggledTrue: 'Archived',
    successToggledFalse: 'Unarchived',
  },
  [ToggleRecordActionTypes.LOCK]: {
    toggleToTrue: 'Lock',
    toggleToFalse: 'Unlock',
    successToggledTrue: 'Locked',
    successToggledFalse: 'Unlocked',
  },
  [ToggleRecordActionTypes.VOID]: {
    toggleToTrue: 'Reject',
    toggleToFalse: 'Un-reject',
    successToggledTrue: 'Rejected',
    successToggledFalse: 'Un-rejected',
  },
  [ToggleRecordActionTypes.CLOSE]: {
    toggleToTrue: 'Close',
    toggleToFalse: 'Re-Open',
    successToggledTrue: 'Closed',
    successToggledFalse: 'Re-Opened',
  },
};

export type ToggleRecordActionStrings = {
  toggleToTrue: string;
  toggleToFalse: string;
  successToggledTrue: string;
  successToggledFalse: string;
};

export interface ToggleRecordDialogProps<
  T extends GenericResource,
  TrueM extends ToggleMutation<T>,
  FalseM extends ToggleMutation<T>
> {
  id: string;
  isCurrentlyTrue: boolean;
  dialogOpen: boolean;
  onClose: () => void;
  recordName: string;
  message?: string;
  additionalContent?: string | ReactNode;
  useToggleTrueMutation: TrueM;
  useToggleFalseMutation: FalseM;
  toggleActionType: ToggleRecordActionTypes;
  onSuccess?: () => void;
  getToggleOnPayload?: TogglePayloadFn<TrueM>;
  getToggleOffPayload?: TogglePayloadFn<FalseM>;
  dialogProps?: Omit<
    NonFormDialogProps,
    'onCloseDialog' | 'dialogOpen' | 'title' | 'open'
  >;
}

/**
 * ToggleRecordDialog
 * To be used specifically with Client V2 api
 * @template T - The resource type extending GenericResource
 * @template TrueM - The toggle true mutation type
 * @template FalseM - The toggle false mutation type
 */
const ToggleRecordDialog = <
  T extends GenericResource,
  TrueM extends ToggleMutation<T>,
  FalseM extends ToggleMutation<T>
>({
  id,
  isCurrentlyTrue,
  dialogOpen,
  onClose,
  recordName,
  message,
  additionalContent,
  useToggleTrueMutation,
  useToggleFalseMutation,
  toggleActionType,
  onSuccess,
  getToggleOnPayload,
  getToggleOffPayload,
  dialogProps,
}: ToggleRecordDialogProps<T, TrueM, FalseM>) => {
  const actionStrings = ToggleActionStrings[toggleActionType];

  const handleSuccess = () => {
    onClose();
    onSuccess && onSuccess();
  };

  const { handleApprove, isLoading, submitError, resetSubmitError } =
    useToggleRecord<T, TrueM, FalseM>({
      useToggleTrueMutation,
      useToggleFalseMutation,
      recordName,
      id,
      isCurrentlyTrue,
      onSuccess: handleSuccess,
      actionStrings: {
        successToggledTrue: actionStrings.successToggledTrue,
        successToggledFalse: actionStrings.successToggledFalse,
      },
      getToggleOnPayload,
      getToggleOffPayload,
    });

  useEffect(() => {
    if (!dialogOpen) {
      resetSubmitError();
    }
  }, [dialogOpen]);

  return (
    <ExtendedDialog
      title={`${
        isCurrentlyTrue
          ? actionStrings.toggleToFalse
          : actionStrings.toggleToTrue
      } ${recordName}`}
      open={dialogOpen}
      onCloseDialog={onClose}
      submitButtonCopy={
        isCurrentlyTrue
          ? actionStrings.toggleToFalse
          : actionStrings.toggleToTrue
      }
      onClickPrimaryButton={handleApprove}
      formSubmitError={submitError}
      isSubmitting={isLoading}
      {...dialogProps}
    >
      <DialogContentText>
        {message ||
          `Are you sure you want to ${(isCurrentlyTrue
            ? actionStrings.toggleToFalse
            : actionStrings.toggleToTrue
          ).toLowerCase()} this ${recordName}?`}
      </DialogContentText>
      {additionalContent &&
        (typeof additionalContent === 'string' ? (
          <DialogContentText sx={{ mt: 2 }}>
            {additionalContent}
          </DialogContentText>
        ) : (
          <Box sx={{ mt: 2 }}>{additionalContent}</Box>
        ))}
    </ExtendedDialog>
  );
};

export default ToggleRecordDialog;
